/**
 * ptキャンペーンの上部の帯
 * 予告中・開催中のキャンペーンを出し、押すと景品画面または企画一覧へ移ります。
 * ログイン後の画面だけが ScreenLayout の topSlot へ渡します。一度もサーバー時刻が分かっていない間は何も出しません。
 * 復帰して取り直す間は、画面が動かないよう最後に分かった時刻で帯を残し、残り時間だけ隠します。
 */

import React, { useRef } from 'react';
import { VISITOR_ROUTES } from '../../../navigation/routeNames';
import { Pressable, Text, View, useWindowDimensions } from '../../../shared/components/WebPrimitives';
import usePointCampaignBoard from '../../../shared/hooks/usePointCampaignBoard';
import useServerNowMs from '../../../shared/hooks/useServerNowMs';
import {
  formatPointCampaignRemaining,
  formatPointCampaignStartsIn,
  selectPointCampaignBandEntries,
  type PointCampaignBandEntry,
} from '../utils/pointCampaignBand';

/** 2件まで・本文の行まで出す画面の高さの下限。これより低い画面では画面の本文の高さを残すため、1件を見出しと残り時間だけで出す */
const BAND_ROOMY_MIN_HEIGHT_PX = 700;

/** 帯の枠と背景 */
const BAND_ROW_CLASS_NAME = 'flex-row items-center gap-2 text-left border-2 border-app-quest-night bg-[#FFE9A8] px-2 py-1 shadow-[2px_2px_0_rgba(22,36,58,0.34)]';

/** キャンペーンの側ごとの表示と移動先 */
const SIDE_DISPLAY = Object.freeze({
  spend: { label: '景品', routeName: VISITOR_ROUTES.REWARDS, destination: '景品一覧' },
  earn: { label: '企画', routeName: VISITOR_ROUTES.EVENTS, destination: '企画一覧' },
});

/** 帯のプロパティ */
type PointCampaignBandProps = {
  navigation: { navigate: (routeName: string) => void };
  /** 表示中の画面のルート名。移動先の画面では押せる形にしない */
  activeRouteName?: string;
};

/**
 * 帯の1行の中身
 * @param {Object} props - プロパティ
 * @param {PointCampaignBandEntry} props.entry - 掲示
 * @param {string | null} props.timeLabel - 残り時間などの文言。時刻を取り直している間はnull
 * @param {number} props.hiddenCount - この行に添える、出しきれなかった件数
 * @param {boolean} props.showBody - 本文を出すか
 * @returns {JSX.Element} 帯の1行の中身
 */
const PointCampaignBandRowContent = ({ entry, timeLabel, hiddenCount, showBody }: {
  entry: PointCampaignBandEntry;
  timeLabel: string | null;
  hiddenCount: number;
  showBody: boolean;
}): React.ReactElement => (
  <>
    <View className={`shrink-0 border-2 border-app-quest-night px-1 py-px ${entry.phase === 'active' ? 'bg-app-quest-night' : 'bg-white'}`}>
      <Text className={`text-[11px] font-extrabold ${entry.phase === 'active' ? 'text-white' : 'text-app-quest-night'}`}>
        {entry.phase === 'active' ? '開催中' : '予告'}
      </Text>
    </View>
    <View className="min-w-0 flex-1">
      <Text className="line-clamp-2 break-words text-sm font-extrabold text-app-quest-night">{entry.item.title}</Text>
      <Text className="break-words text-xs font-bold text-app-quest-night">
        {`${SIDE_DISPLAY[entry.item.side].label}${timeLabel ? `・${timeLabel}` : ''}${hiddenCount > 0 ? `・ほか${hiddenCount}件` : ''}`}
      </Text>
      {showBody && entry.item.body ? (
        <Text className="line-clamp-1 break-words text-xs text-app-text">{entry.item.body}</Text>
      ) : null}
    </View>
  </>
);

/**
 * ptキャンペーンの上部の帯
 * @param {PointCampaignBandProps} props - プロパティ
 * @returns {JSX.Element | null} 帯。出す掲示が無ければnull
 */
const PointCampaignBand = ({ navigation, activeRouteName }: PointCampaignBandProps): React.ReactElement | null => {
  /** 全画面共通の掲示 */
  const { items } = usePointCampaignBoard();
  /** サーバー時刻 */
  const nowMs = useServerNowMs();
  /** 表示領域の高さ */
  const { height } = useWindowDimensions();
  /** 最後に分かったサーバー時刻 */
  const lastKnownNowMsRef = useRef<number | null>(null);
  if (nowMs !== null) lastKnownNowMsRef.current = nowMs;
  /** 帯の判定に使う時刻 */
  const displayNowMs = nowMs ?? lastKnownNowMsRef.current;

  if (displayNowMs === null) return null;

  /** 帯に出せる掲示 */
  const entries = selectPointCampaignBandEntries(items, displayNowMs);
  if (entries.length === 0) return null;

  /** 実際に出す掲示 */
  const visibleEntries = entries.slice(0, height >= BAND_ROOMY_MIN_HEIGHT_PX ? 2 : 1);
  /** 出しきれなかった件数 */
  const hiddenCount = entries.length - visibleEntries.length;
  /** 本文の行を出すか。2件並ぶ時と高さの低い画面は省く（読み上げ名には全文を入れる） */
  const showBody = height >= BAND_ROOMY_MIN_HEIGHT_PX && visibleEntries.length === 1;

  return (
    <View accessibilityLabel="ptキャンペーンのお知らせ" className="mb-2 shrink-0 gap-1" role="region" testID="point-campaign-band">
      {visibleEntries.map((entry, index) => {
        /** 残り時間などの文言 */
        const timeLabel = nowMs === null ? null : entry.phase === 'active'
          ? formatPointCampaignRemaining(entry.item.endsAtMs - nowMs)
          : formatPointCampaignStartsIn(entry.item.startsAtMs - nowMs);
        /** 側ごとの表示と移動先 */
        const sideDisplay = SIDE_DISPLAY[entry.item.side];
        /** 行の中身 */
        const content = (
          <PointCampaignBandRowContent
            entry={entry}
            hiddenCount={index === visibleEntries.length - 1 ? hiddenCount : 0}
            showBody={showBody}
            timeLabel={timeLabel}
          />
        );
        return sideDisplay.routeName === activeRouteName ? (
          <View className={BAND_ROW_CLASS_NAME} key={entry.item.id}>{content}</View>
        ) : (
          <Pressable
            accessibilityHint={`${sideDisplay.destination}を開きます`}
            accessibilityLabel={`${entry.phase === 'active' ? '開催中' : '予告'} ${entry.item.title}。${timeLabel ? `${timeLabel}。` : ''}${entry.item.body}${index === visibleEntries.length - 1 && hiddenCount > 0 ? `。ほか${hiddenCount}件` : ''}`}
            accessibilityRole="button"
            className={`min-h-11 ${BAND_ROW_CLASS_NAME} active:translate-x-[1px] active:translate-y-[1px] active:shadow-none`}
            key={entry.item.id}
            onPress={() => navigation.navigate(sideDisplay.routeName)}
          >
            {content}
            <Text accessible={false} className="shrink-0 text-base font-extrabold text-app-quest-night">›</Text>
          </Pressable>
        );
      })}
    </View>
  );
};

export default PointCampaignBand;
